import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Close from '@mui/icons-material/Close';
import Warning from '@mui/icons-material/Warning';
import { ContextualHelpModule } from '@qbe/ads-blocks-forms';
import { AppBar, Divider, Icon, IconButton, PageLayout, Stack, Typography } from '@qbe/ads-core';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toSitecoreUrl } from 'utils/toSitecoreUrl';

import Footer from '~/components-ds/Footer';
import Modal from '~/components-ds/Modal';
import Stepper, {
  coverDetailsStepToCover,
  coverToRoute,
  DynamicStep,
  dynamicSteps,
  StaticStep,
  stepToPreviousRoute,
} from '~/components-ds/Stepper';
import { TargetEnv, targetEnv } from '~/config';
import { useAppDispatch, useAppNavigate, useAppSelector, useSelectCover, useStep } from '~/hooks';
import { useAppContent } from '~/hooks/useAppContent';
import { selectIsReferralVelocity } from '~/selectors/selectAppDetails';
import { clearPaymentErrors } from '~/slices/payments';
import { qalTrackEngagementAction } from '~/thunks/analytics';
import { navigateAction } from '~/thunks/navigateAction';
import { Cover } from '~/types/cover';
import { Step } from '~/types/enums';
import { AppRoute } from '~/types/types';

import { WelcomeBadge } from '../../components/WelcomeBadge';
import { BUSINESS_DETAILS_ROUTE, MEMBERSHIP_ROUTE } from '../../constants/routes';
import FloatingContactUs from '../FloatingContactUs';
import { useHelperText } from '../HelperTextProvider/useHelperText';
import AppLoading from './AppLoading';
import PageBackground from './PageBackground';

/**
 * @note we don't have to worry about the last cover causing an index out of bounds error
 * as the below util will have the smarts to only call it when it needs the next route
 * and if it is the last route, it will just use the quote route
 */
const getPreviousRoute = (
  currentStep: DynamicStep,
  covers: Cover[],
  hasMembershipStep: boolean = false
): AppRoute => {
  /* If the current step is quote, calculate the route for the last cover  */
  if (currentStep === Step.Quote) {
    if (hasMembershipStep) {
      return MEMBERSHIP_ROUTE;
    }
    return coverToRoute[covers[covers.length - 1]];
  }

  /**
   * @note we never have to worry about covers[Cover.Liability] because it is handled as a static step
   * and thus, we will never experience an index out of bounds exception
   */
  /*  Otherwise just calculate the previous cover from the currentStep */
  const previousCover = covers[covers.indexOf(coverDetailsStepToCover[currentStep]) - 1];

  if (!previousCover) throw new Error('Unable to retrieve a previous cover');

  return coverToRoute[previousCover];
};

type AppLayoutProps = {
  children: React.ReactNode | Array<React.ReactNode>;
  showStepper?: boolean;
};

export const AppLayout: React.FC<AppLayoutProps> = ({ children, showStepper = true }) => {
  const { getTextContent: getSharedText } = useAppContent({ basePath: 'shared' });
  const { getTextContent: getExitModalText } = useAppContent({ basePath: 'dsme.exitModal' });

  const { pathname } = useLocation();

  const { currentStep } = useStep();
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { covers } = useSelectCover();
  const { status } = useAppContent();

  const [showExitModal, setShowExitModal] = useState(false);
  const hasMembership = useAppSelector(selectIsReferralVelocity);

  const handleBack = () => {
    if (dynamicSteps.includes(currentStep)) {
      dispatch(
        navigateAction({
          to: getPreviousRoute(currentStep as DynamicStep, covers, hasMembership),
          navigate,
        })
      );
    } else {
      if (currentStep === Step.Payments) dispatch(clearPaymentErrors());
      dispatch(
        navigateAction({
          to: stepToPreviousRoute[currentStep as StaticStep],
          navigate,
        })
      );
    }
  };

  const hideBackButton =
    currentStep === Step.BusinessDetails ||
    currentStep === Step.Review ||
    currentStep === Step.Confirmation;

  const hideCloseButton = currentStep === Step.Confirmation;

  const { currentHelpers, closeHelper } = useHelperText();

  return (
    <>
      <PageLayout
        rightAside={currentHelpers.map((helper) => (
          <ContextualHelpModule
            key={helper.id}
            isOpen={helper.open}
            handleClose={closeHelper(helper.id)}
            offsetY={helper.offsetY !== undefined ? `${helper.offsetY}px` : undefined}
            overlayBreakpoint="1200px"
          >
            {helper.content}
          </ContextualHelpModule>
        ))}
        helperText={<FloatingContactUs />}
        header={
          <AppBar
            brand={
              <div style={{ width: '120px', display: 'flex', alignItems: 'center' }}>
                <Icon
                  src={toSitecoreUrl('2D74F65DD44C49F9A53CB258D9F6DE31')}
                  variant="img"
                  style={{ width: '100%', height: 'auto' }}
                />
              </div>
            }
            stepper={showStepper ? <Stepper /> : undefined}
          >
            {showStepper && (
              <Stack
                direction="row"
                divider={<Divider orientation="vertical" flexItem color="white" />}
                spacing={1}
              >
                {hideBackButton ? null : (
                  <IconButton
                    data-track="header: back"
                    onClick={handleBack}
                    iconProps={
                      {
                        muiIcon: ChevronLeftIcon,
                        size: 'large',
                        color: 'white',
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      } as any
                    }
                  />
                )}

                {pathname === BUSINESS_DETAILS_ROUTE && <WelcomeBadge />}

                {hideCloseButton ? null : (
                  <IconButton
                    data-track="header: exit"
                    onClick={() => setShowExitModal(true)}
                    // TODO error in console - https://qbe-appservices.atlassian.net/browse/AD1-545
                    iconProps={
                      {
                        muiIcon: Close,
                        size: 'large',
                        color: 'white',
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      } as any
                    }
                  />
                )}
              </Stack>
            )}
          </AppBar>
        }
        footer={<Footer />}
      >
        {targetEnv !== TargetEnv.prod ? <PageBackground /> : null}
        {status === 'ready' ? children : <AppLoading />}
      </PageLayout>

      <Modal
        id="exit-modal"
        open={showExitModal}
        analyticsModalName="header exit modal"
        title={getExitModalText('title')}
        primaryBtnProps={{
          label: getExitModalText('buttons.primaryAction'),
          onClick: () => {
            setShowExitModal(false);
            dispatch(
              qalTrackEngagementAction({
                eventAction: 'modal: close',
                eventLabel: 'header exit',
                data: {},
              })
            );
          },
          'data-track': 'Back to quote',
        }}
        secondaryBtnProps={{
          label: getExitModalText('buttons.secondaryAction'),
          onClick: () => window.location.replace(getSharedText('qbeHome')),
          'data-track-label': 'Go to QBE',
          'data-track': 'Go to QBE',
        }}
        iconProps={{
          muiIcon: Warning,
          color: '#d43900',
        }}
      >
        <Typography>{getExitModalText('description')}</Typography>
      </Modal>
    </>
  );
};
